import React from "react"
import { graphql } from "gatsby"
import { StructuredText } from "react-datocms"
import {
  isHeading,
  isParagraph,
  renderRule,
} from "datocms-structured-text-utils"
import {
  Box,
  Text,
  Container,
  Flex,
  Grid,
  Heading,
  Image,
  Link,
} from "@theme-ui/components"
import Layout from "../components/layout"
import { getArticlePath } from "../utils/path"
import { GatsbyImage } from "gatsby-plugin-image"
import ImageGallery from "../components/blocks/imageGallery"
import ArticleTitle from "../components/articleTitle"
import { HelmetDatoCms } from "gatsby-source-datocms"
import ArticleHero from "../templates/articleHero"
import ArticleSummary from "../components/articleSummary"
import { InboundLink } from "../components/link"
import CtaCollection from "../components/ctaCollection"
import Facebook from "../components/facebook"
import Linkedin from "../components/linkedin"
import Chain from "../images/chain"
import SingleAccordion from "../components/blocks/singleAccordion"
import Embed from "../components/blocks/embed"
import { useColorMode } from "theme-ui"
import ListArrowViolet from "../images/list-arrow-violet.svg"
import { useState } from "react"
import ArticleBanner from "../components/articleBanner"
import CaseCollection from "../components/caseCollection"
import Survey from "../components/Survey"

const Article = ({
  data: { page, site, footer, menu, partner },
  pageContext,
}) => {
  const locale = pageContext.locale
  const colorMode = useColorMode()

  const [formData, setFormData] = useState({
    nome: "",
    cognome: "",
    azienda: "",
    telefono: "",
    email: "",
    messaggio: "",
  })


  const i18nPaths = page._allSlugLocales.map(path => {
    return {
      locale: path.locale,
      value: getArticlePath(page, path.locale),
    }
  })
  console.log(colorMode, "colorMode")
  // Costruisci l'URL completo dell'articolo per i social share (senza dipendere da window)
  const articleUrl = `https://diginnova.it${getArticlePath(page, locale)}`

  return (
    <Layout
      locale={locale}
      i18nPaths={i18nPaths}
      footerData={footer.nodes}
      menuData={menu.nodes}
      partner={partner}
    >
      <HelmetDatoCms seo={page.seoMetaTags}>
        <html lang={page.locale} />
      </HelmetDatoCms>
      <ArticleHero article={page} />

      <Container
        variant="lg"
        sx={{ padding: ["20px ", 0, 0, 0], mt: ["0px", "0px", "90px"] }}
      >
        <Grid columns={[1, 1, "1fr 3fr 1fr"]} gap={[0, 0, 0, "80px"]} sx={{}}>
          <Box>
            <ArticleSummary structuredTextData={page.body} />
          </Box>

          <Box
            sx={{
              flex: 1,
              color: "dark",
              p: {
                fontSize: "21px",
                fontWeight: 400,
                lineHeight: "1.2",
                a: {
                  color: "primary",
                  textDecoration: "underline",
                },
              },
              ul: {
                padding: "0 0 0 20px",
              },
              ol: {
                padding: "0 0 0 20px",
              },
              blockquote: {
                fontSize: "24px",
                p: {
                  fontSize: [
                    "24px",
                    "24px",
                    "24px!important",
                    "24px!important",
                  ],
                  fontWeight: 400,
                  lineHeight: "1.25",
                },
                em: {
                  fontStyle: "italic",
                  fontSize: "24px",
                },
                mx: 0,
                my: "40px",
                py: "40px",

                borderTop: "2px solid",
                borderBottom: "2px solid",
                borderColor: "primary",
                color: "primary",
              },
              h3: {
                fontSize: ["24px", "24px", "34px", "34px"],
                fontWeight: 400,
                lineHeight: "1.2",
              },
              h2: {

                letterSpacing: "0.5",
                mb: "40px",
                mt: ["40px", "40px", "40px"],
                color: "primary",
                width: "fit-content",
                maxWidth: "fit-content",
                minWidth: "fit-content",
                borderColor: "primary",
                fontSize: "34px",
                fontWeight: 400,
                scrollMarginTop: "150px", // Important for anchor links with sticky header
              },
            }}
          >
            <StructuredText
              data={page.body}
              customNodeRules={[
                renderRule(
                  node => isHeading(node) && node.level === 2,
                  ({ adapter: { renderNode }, node, children, key }) => {
                    const slug = node.children[0].value
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, "-")
                      .replace(/(^-|-$)/g, "")

                    return renderNode(
                      () => <h2 id={slug}>{children}</h2>,
                      { key },
                      children
                    )
                  }
                ),
                renderRule(
                  isParagraph,
                  ({ adapter: { renderNode }, node, children, key }) => {
                    return renderNode(
                      () => {
                        return (
                          <Text as="p" mb={3} variant="article">
                            {children}
                          </Text>
                        )
                      },
                      { key },
                      children
                    )
                  }
                ),
              ]}
              renderBlock={({ record }) => {
                // console.log(record)
                switch (record.__typename) {
                  case "DatoCmsCaseCollection":
                    return (
                      <Box
                        sx={{
                          mb: "60px",
                          mt: "60px",
                        }}
                      >
                        <CaseCollection record={record} />
                      </Box>
                    )
                  case "DatoCmsBanner":
                    return (
                      <Box sx={{ mb: "60px", mt: ["60px", "60px", "60px"] }}>
                        <ArticleBanner
                          title={record.title}
                          subtitle={record.subtitle}
                          documents={record.document?.documents}
                          link={record.link}
                          locale={locale}
                        />
                      </Box>
                    )
                  case "DatoCmsAccordion":
                    return (
                      <Box sx={{ mb: "24px" }}>
                        <SingleAccordion
                          title={record.title}
                          subtitle={record.subtitle}
                          body={record.body}
                          key={record.id}
                        />
                      </Box>
                    )
                  case "DatoCmsList":
                    return (
                      <Box mt={3} mb={3}>
                        <Box
                          dangerouslySetInnerHTML={{ __html: record.body }}
                          sx={{
                            ul: {
                              listStyle: "none",
                              paddingLeft: 0,
                              li: {
                                position: "relative",
                                paddingLeft: "60px",
                                marginBottom: "10px",
                                "&::before": {
                                  content: '""',
                                  position: "absolute",
                                  left: 0,
                                  right: "10px",
                                  top: "0px",
                                  width: "40px",
                                  height: "30px",
                                  backgroundImage: `url(${ListArrowViolet})`,
                                  backgroundSize: "contain",
                                  backgroundRepeat: "no-repeat",
                                  backgroundPosition: "center",
                                },
                              },
                            },
                          }}
                        />
                      </Box>
                    )
                  case "DatoCmsImageGallery":
                    return (
                      <Box mt={5} mb={5}>
                        {colorMode[0] === "dark" && record.imagesDark ? (
                          <Box>
                            <GatsbyImage
                              image={record.imagesDark.gatsbyImageData}
                              alt={record.imagesDark.title || ""}
                            />
                            {record.imagesDark.title && (
                              <Text variant="caption">
                                {record.imagesDark.title}
                              </Text>
                            )}
                          </Box>
                        ) : (
                          <ImageGallery
                            images={record.images}
                            key={record.id}
                          />
                        )}
                      </Box>
                    )
                  case "DatoCmsEmbed":
                    return (
                      <Box sx={{ mb: "60px", mt: "60px" }}>
                        <Embed code={record.code} fullWidth={record.fullWidth} />
                      </Box>
                    )
                  default:
                    return null
                }
              }}
            />
            {page.survey && (
              <Survey
                survey={page.survey}
                formData={formData}
                setFormData={setFormData}
              />
            )}
          </Box>

          {page.related && page.related.length > 0 && (
            <Box
              sx={{
                width: ["100%", "100%", "280px"],
                flexShrink: 0,
                display: ["none", "none", "block"],
              }}
            >
              <Box
                sx={{
                  position: "sticky",
                  top: "120px",
                  maxHeight: "calc(100vh - 140px)",
                  overflowY: "auto",
                }}
              >
                <Flex sx={{ mt: "40px", flexDirection: "column" }}>
                  <Text
                    sx={{
                      pb: "16px",
                      fontSize: "18px",
                      fontWeight: 500,
                      color: "dark",
                    }}
                  >
                    CORRELATI
                  </Text>
                  <Flex sx={{ flexDirection: "column" }}>
                    {page.related.filter(article => article && article.slug).map((article, index) => (
                      <Box
                        key={index}
                        sx={{
                          py: "15px",
                          borderTop: "1px solid",
                          borderColor: "dark",
                          a: {
                            color: "dark",
                            display: "flex",
                            justifyContent: "space-between",
                            gap: "30px",
                            textDecoration: "none",
                            "&:hover": {
                              textDecoration: "underline",
                            },
                          },
                        }}
                      >
                        <InboundLink to={getArticlePath(article, locale)}>
                          <Heading
                            sx={{
                              fontSize: "16px",
                              flexBasis: "70%",
                              color: "dark",
                            }}
                          >
                            {" "}
                            {article.title}
                          </Heading>

                          <Image
                            sx={{
                              aspectRatio: "1/1",
                              flexBasis: "60px",
                              width: "60px",
                              height: "60px",
                            }}
                            src={article.heroImage.url}
                          />
                        </InboundLink>
                      </Box>
                    ))}
                  </Flex>
                </Flex>
              </Box>
            </Box>
          )}

          {/* Social/copia link SEMPRE visibile */}
          <Box
            sx={{
              width: ["100%", "100%", "280px"],
              flexShrink: 0,
              display: ["none", "none", "block"],
              mt: page.related && page.related.length > 0 ? 0 : "40px",
              position:
                page.related && page.related.length > 0 ? "static" : "sticky",
              top:
                page.related && page.related.length > 0 ? undefined : "120px",
            }}
          >
            <Flex
              sx={{
                flexDirection: ["column", "column"],
                gap: "10px",
                alignSelf: "flex-start",
              }}
            >
              <Link
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                  articleUrl
                )}&t=${encodeURIComponent(page.title)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Facebook color={colorMode[0]} />
              </Link>
              <Link
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                  articleUrl
                )}&title=${encodeURIComponent(page.title)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Linkedin color={colorMode[0]} />
              </Link>
              <Chain color={colorMode[0]} url={articleUrl} />
            </Flex>
          </Box>
        </Grid>

        {/* Questionnaire and Form Section */}
      </Container>
      <Box>
        {page.content.map(
          block =>
            block.model.apiKey === "cta_collection" && (
              <CtaCollection block={block} />
            )
        )}
      </Box>
    </Layout>
  )
}

export default Article

export const query = graphql`
  query ArticleQuery($id: String!, $locale: String!) {
    page: datoCmsArticle(id: { eq: $id }, locale: $locale) {
      ...ArticleDetails
      content {
        ... on DatoCmsCtaCollection {
          id
          cta {
            title
            subtitle
            link {
              anchor
              link {
                ... on DatoCmsPage {
                  root
                  id
                  _allSlugLocales {
                    locale
                    value
                  }
                }
              }
            }
          }
          model {
            apiKey
          }
        }
      }
      ...ArticleAllSlugLocales
      ...ArticleMeta
      meta {
        firstPublishedAt(locale: $locale, formatString: "DD MMMM Y")
      }
      seoMetaTags {
        ...GatsbyDatoCmsSeoMetaTags
      }
    }
    partner: allDatoCmsPartner(sort: { position: ASC }) {
      ...PartnerFragment
    }

    menu: allDatoCmsMenu(
      locale: $locale
      filter: { root: { eq: true }, locales: { eq: $locale } }
      sort: { position: ASC }
    ) {
      nodes {
        ...MenuDetails
      }
    }

    footer: allDatoCmsFooter(
      locale: $locale
      filter: { root: { eq: true }, locales: { eq: $locale } }
      sort: { position: ASC }
    ) {
      nodes {
        ...FooterDetails
      }
    }
  }

  fragment ArticleMeta on DatoCmsArticle {
    meta {
      firstPublishedAt(locale: $locale, formatString: "DD MMMM Y")
    }
  }

  fragment ArticleAllSlugLocales on DatoCmsArticle {
    _allSlugLocales {
      value
      locale
    }
  }

  fragment ArticleDetails on DatoCmsArticle {
    id
    locales
    title
    slug
    model {
      apiKey
    }
    related {
      _allTitleLocales {
        locale
        value
      }
      title
      slug
      heroImage {
        url
        gatsbyImageData
      }
    }
    category {
      title
      slug
      link {
        title
        root
        model {
          apiKey
        }
        _allSlugLocales {
          locale
          value
        }
        slug
      }
      ...ArticleCategoryAllSlugLocales
    }
    heroImage {
      gatsbyImageData(
        width: 1480
        height: 986
        placeholder: BLURRED
        forceBlurhash: false
      )
    }
    survey {
      id
      kicker
      title
      questions {
        controlType
        id
        text
        answers {
          id
          text
        }
      }
    }
    ...ArticleBody
  }

  fragment ArticleBody on DatoCmsArticle {
    body {
      value
      blocks {
        __typename
        ... on DatoCmsImageGallery {
          id: originalId
          ...ImageGallery
        }
        ... on DatoCmsAccordion {
          id: originalId
          ...Accordion
        }
        ... on DatoCmsBanner {
          id: originalId
          title
          subtitle
          link {
            ... on DatoCmsInternalLink {
              id
              anchor
              model {
                apiKey
              }
              link {
                ... on DatoCmsPage {
                  id
                  root
                  _allSlugLocales {
                    locale
                    value
                  }
                  treeParent {
                    root
                    _allSlugLocales {
                      locale
                      value
                    }
                    treeParent {
                      root
                      _allSlugLocales {
                        locale
                        value
                      }
                    }
                  }
                }
                ... on DatoCmsArticle {
                  slug
                  _allSlugLocales {
                    locale
                    value
                  }
                }
              }
            }
            ... on DatoCmsExternalLink {
              id
              anchor
              url
              model {
                apiKey
              }
            }
          }
          document {
            title
            documents {
              url
              title
            }
          }
        }
        ... on DatoCmsList {
          id: originalId
          body
        }
        ... on DatoCmsCaseCollection {
          id: originalId
          cases {
            ... on DatoCmsExternalCase {
              id
              title
              subtitle
              image {
                url
                gatsbyImageData(
                  width: 400
                  height: 200
                  placeholder: BLURRED
                  forceBlurhash: false
                )
              }
              model {
                apiKey
              }
              link {
                ... on DatoCmsInternalLink {
                  id
                  anchor
                  model {
                    apiKey
                  }
                  link {
                    ... on DatoCmsPage {
                      id
                      root
                      _allSlugLocales {
                        locale
                        value
                      }
                      treeParent {
                        root
                        _allSlugLocales {
                          locale
                          value
                        }
                        treeParent {
                          root
                          _allSlugLocales {
                            locale
                            value
                          }
                        }
                      }
                    }
                    ... on DatoCmsArticle {
                      slug
                      _allSlugLocales {
                        locale
                        value
                      }
                    }
                  }
                }
                ... on DatoCmsExternalLink {
                  id
                  anchor
                  url
                  model {
                    apiKey
                  }
                }
              }
            }
            ... on DatoCmsInternalCase {
              id
              title
              subtitle
              image {
                url
                gatsbyImageData(
                  width: 400
                  height: 200
                  placeholder: BLURRED
                  forceBlurhash: false
                )
              }
              model {
                apiKey
              }
              case {
                _allSlugLocales {
                  locale
                  value
                }
                title
                slug
              }
            }
          }
        }
        ... on DatoCmsEmbed {
          id: originalId
          code
          fullWidth
        }
      }
    }
  }

  fragment Accordion on DatoCmsAccordion {
    title
    subtitle
  }

  fragment ImageGallery on DatoCmsImageGallery {
    images {
      gatsbyImageData(width: 1480, placeholder: BLURRED, forceBlurhash: false)
      alt
      title
    }
    imagesDark {
      gatsbyImageData(width: 1480, placeholder: BLURRED, forceBlurhash: false)
      alt
      title
    }
    model {
      apiKey
    }
  }
`
